import React, { useMemo, useRef, useLayoutEffect, useState } from "react";

// Ad preview that matches the printed newspaper Public-Notice format:
//  - Heavy double border around the whole ad
//  - Black-bar reverse-text headline ("Public Notice"/"जाहीर प्रकटन"/"सार्वजनिक सूचना")
//  - Body justified in serif; font size auto-GROWS to fill the entire box
//    (no negative space — short notices grow, long ones shrink)
//  - Footer with "Through" + bold "Adv. <name>" + address + Mob

const HEADINGS = { english: "Public Notice", marathi: "जाहीर प्रकटन", hindi: "सार्वजनिक सूचना" };
const THROUGH  = { english: "Through",        marathi: "मार्फत",     hindi: "के माध्यम से" };
const ADV_LABEL = { english: "Adv.",          marathi: "अ‍ॅड.",      hindi: "अधि." };

export default function AdPreview({
  language = "english",
  body = "",
  advocate = {},
  paperName,
  publishDate,
  adType = "public_notice",
  conSubtype = "name_only",
  relation = "self",
  armyNo = "",
  rankLabel = "",
  oldName = "",
  newName = "",
  oldDob = "",
  newDob = "",
  affidavitNumber = "",
  affidavitDate = "",
}) {
  const heading = HEADINGS[language] || HEADINGS.english;
  const through = THROUGH[language] || THROUGH.english;
  const advLabel = ADV_LABEL[language] || ADV_LABEL.english;
  const phones = [advocate.contact_primary, advocate.contact_secondary, advocate.contact_tertiary].filter(Boolean);
  const isChangeOfName = adType === "change_of_name";
  const isNameDob = isChangeOfName && conSubtype === "name_and_dob";

  // Refs we measure to grow/shrink the body font to fully fill the box.
  const bodyRef = useRef(null);
  const measureRef = useRef(null);
  const [bodyFontPx, setBodyFontPx] = useState(8);

  // Re-fit whenever the body text or advocate block changes.
  useLayoutEffect(() => {
    const container = bodyRef.current;
    const probe = measureRef.current;
    if (!container || !probe) return;
    const availableHeight = container.clientHeight;
    const availableWidth  = container.clientWidth;
    if (availableHeight < 10 || availableWidth < 10) return;

    let lo = 4, hi = 18, best = lo;
    while (lo <= hi) {
      const mid = (lo + hi) / 2;
      probe.style.fontSize    = `${mid}px`;
      probe.style.lineHeight  = `${Math.max(mid * 1.18, mid + 1.4)}px`;
      probe.style.width       = `${availableWidth}px`;
      if (probe.scrollHeight <= availableHeight) { best = mid; lo = mid + 0.25; }
      else { hi = mid - 0.25; }
      if (hi - lo < 0.2) break;
    }
    setBodyFontPx(best);
  }, [body, language, advocate.name, advocate.address, phones.join(",")]);

  return (
    <div className="bg-white" data-testid="ad-preview">
      <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold mb-2 flex flex-wrap items-center gap-2">
        <span>Ad Preview</span>
        {paperName && <span>· {paperName}</span>}
        <span>· {language.charAt(0).toUpperCase() + language.slice(1)}</span>
        {isChangeOfName
          ? <span>· 15 sq cm  (3 cm × 5 cm)</span>
          : <span>· 40 sq cm  (4 cm × 10 cm)</span>}
      </div>

      {isChangeOfName ? (
        // ---- Change of Name — 3 × 5 classified matching the printed sample ----
        <div
          className="mx-auto"
          style={{
            width: "min(200px, 100%)",
            aspectRatio: "3 / 5",
            padding: 4,
            background: "#000",
          }}
          data-testid="ad-preview-change-of-name"
        >
          <div
            className="w-full h-full bg-white flex flex-col"
            style={{
              border: "2px solid #000",
              fontFamily: '"Times New Roman", Georgia, serif',
              color: "#000",
            }}
          >
            <div
              className="text-center font-bold tracking-wider shrink-0"
              style={{
                background: "#000", color: "#fff",
                padding: "6px 4px",
                fontSize: isNameDob ? 12 : 15, lineHeight: 1.1, letterSpacing: "0.04em",
              }}
              data-testid="con-heading"
            >
              {isNameDob ? "CHANGE OF NAME AND DATE OF BIRTH" : "CHANGE OF NAME"}
            </div>
            <div
              className="px-2 pt-2 pb-2 flex-1 overflow-hidden"
              style={{
                textAlign: "justify",
                fontSize: isNameDob ? 8 : 9,
                lineHeight: 1.32,
                hyphens: "auto",
                wordBreak: "break-word",
              }}
              data-testid="con-body"
            >
              {isNameDob ? (
                <>
                  {"I, "}
                  {armyNo && <>Army No. <strong>{armyNo}</strong>, </>}
                  {rankLabel && <>Rank-<strong>{rankLabel}</strong>, </>}
                  {"Name : "}
                  <strong>{(advocate.name || "DECLARANT NAME").toUpperCase()}</strong>
                  {advocate.address && <>{" presently resident at "}{advocate.address}, </>}
                  {" is hereby declare that I have changed "}
                  {relation === "self" ? "my" : relation === "mother" ? "my Mother" : relation === "father" ? "my Father" : "my Child's"}
                  {" name from "}
                  <strong>{(oldName || "OLD NAME").toUpperCase()}</strong>
                  {" to "}
                  <strong>{(newName || "NEW NAME").toUpperCase()}</strong>
                  {" & "}
                  {relation === "self" ? "my" : "Her/His"}
                  {" Date of Birth from "}
                  <strong>{oldDob || "OLD DOB"}</strong>
                  {" to "}
                  <strong>{newDob || "NEW DOB"}</strong>
                  {". Affidavit No. "}
                  <strong>{affidavitNumber || "XXXXXXXX"}</strong>
                  {affidavitDate ? ` dated ${affidavitDate}` : ""}
                </>
              ) : (
                <>
                  {"I, "}
                  {armyNo && <>Army No. <strong>{armyNo}</strong>, </>}
                  {rankLabel && <>Rank-<strong>{rankLabel}</strong>, </>}
                  <strong>{(advocate.name || (oldName || "OLD NAME")).toUpperCase()}</strong>
                  {" Hereby Declare That I Have Officially Changed "}
                  {relation === "self" ? "My Name" : relation === "mother" ? "My Mother's Name" : relation === "father" ? "My Father's Name" : "My Child's Name"}
                  {" From "}
                  <strong>{(oldName || "OLD NAME").toUpperCase()}</strong>
                  {" To "}
                  <strong>{(newName || "NEW NAME").toUpperCase()}</strong>
                  {" Through Affidavit No "}
                  <strong>{affidavitNumber || "XXXXXXXX"}</strong>
                  {affidavitDate ? ` On ${affidavitDate}` : ""}
                  {". Henceforth "}
                  {relation === "self" ? "I Will" : "They Will"}
                  {" Be Known As "}
                  <strong>{(newName || "NEW NAME").toUpperCase()}</strong>
                  {"."}
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
      // ---- Public Notice — 4 × 10 (existing layout) ----
      <div
        className="mx-auto"
        style={{
          width: "min(260px, 100%)",
          aspectRatio: "4 / 10",
          padding: 3,
          background: "#000",
        }}
      >
        <div
          className="w-full h-full bg-white flex flex-col"
          style={{
            border: "1px solid #000",
            fontFamily: '"Times New Roman", "Lohit Devanagari", Georgia, serif',
            color: "#000",
          }}
        >
          {/* HEADER — black bar, white reverse text */}
          <div
            className="text-center font-bold tracking-wide shrink-0"
            style={{
              background: "#000",
              color: "#fff",
              padding: "5px 4px",
              fontSize: language === "english" ? 14 : 15,
              lineHeight: 1.05,
              letterSpacing: language === "english" ? "0.02em" : "0.03em",
            }}
            data-testid="ad-preview-heading"
          >
            {heading}
          </div>

          {/* BODY — flex-1, fills all remaining vertical space; font auto-sized */}
          <div
            ref={bodyRef}
            className="px-1.5 pt-1 pb-1 flex-1 overflow-hidden"
            style={{
              textAlign: "justify",
              fontSize: bodyFontPx,
              lineHeight: `${Math.max(bodyFontPx * 1.18, bodyFontPx + 1.4)}px`,
              hyphens: "auto",
              wordBreak: "break-word",
            }}
            data-testid="ad-preview-body"
          >
            {body && body.trim().length > 0
              ? body
              : <span className="text-gray-400 italic">Your notice text appears here.</span>}
          </div>

          {/* Off-screen probe used by the auto-fit binary search */}
          <div
            ref={measureRef}
            aria-hidden="true"
            style={{
              position: "absolute", visibility: "hidden",
              top: 0, left: -99999,
              textAlign: "justify",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: '"Times New Roman", "Lohit Devanagari", Georgia, serif',
            }}
          >{body}</div>

          {/* FOOTER */}
          <div className="px-1.5 pt-1 pb-1.5 text-center shrink-0" style={{ fontSize: 6, lineHeight: 1.2 }}>
            <div className="text-right" style={{ fontSize: 7 }}>{through}</div>
            <div
              style={{
                fontWeight: 800,
                fontSize: language === "english" ? 11 : 12,
                lineHeight: 1.05,
                letterSpacing: "0.02em",
                margin: "1px 0 2px 0",
              }}
              data-testid="ad-preview-advname"
            >
              {advocate.profession === "Advocate" ? `${advLabel} ` : ""}{advocate.name || "Your Name"}
            </div>
            {advocate.address && (
              <div style={{ fontSize: 6.5, lineHeight: 1.22 }}>{advocate.address}</div>
            )}
            {phones.length > 0 && (
              <div style={{ fontSize: 6.5, lineHeight: 1.22, marginTop: 1 }}>
                Mob: {phones.join(", ")}
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      <div className="text-[10px] text-gray-500 mt-2 italic text-center">
        Final layout may vary marginally by paper. Publication subject to newspaper approval.
        {publishDate && <> · To be published on <strong>{publishDate}</strong>.</>}
        {isChangeOfName && <> · <strong className="text-black">This ad will be published subject to verification.</strong></>}
      </div>
    </div>
  );
}
